import { useState } from "react";
import { Sun, Edit3, Trash2, Download, MoreVertical, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { downloadEntryAsPdf } from "@/components/PdfExporter";
import { MOOD_EMOJIS } from "@/utils/constants";
import { useTheme } from "@/context/ThemeContext";
import { useFeatures } from "@/context/FeaturesContext";
import { PricingModal } from "@/components/UpgradeBanner";

// Animated gradient glow for UnCloned mood
const UnClonedMoodIcon = ({ size = "md" }) => {
  const sizeClass = size === "lg" ? "w-12 h-12" : "w-10 h-10";
  return (
    <>
      <style>{`
        @keyframes uncloned-gradient-card {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .uncloned-glow-card {
          animation: uncloned-gradient-card 3s ease infinite;
        }
      `}</style>
      <div 
        className={`${sizeClass} uncloned-glow-card`}
        style={{
          background: 'linear-gradient(90deg, var(--theme-primary) 0%, var(--theme-secondary) 25%, #D0FF0B 50%, #0FFFE2 75%, var(--theme-primary) 100%)',
          backgroundSize: '200% 100%',
          WebkitMaskImage: 'url("/mood-uncloned.png")',
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskImage: 'url("/mood-uncloned.png")',
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          filter: 'drop-shadow(0 0 8px rgba(var(--theme-primary-rgb), 0.5)) drop-shadow(0 0 16px rgba(var(--theme-secondary-rgb), 0.3))',
        }}
      />
    </>
  );
};

// Helper to render mood emoji or custom image
const MoodDisplay = ({ moodData, className = "mood-display", getEmoji }) => {
  if (!moodData) return <span className={className}>✨</span>;
  
  // If custom mood has an image URL, show the image
  if (moodData.imageUrl) {
    return (
      <img 
        src={moodData.imageUrl} 
        alt={moodData.label || 'Custom Mood'} 
        className="w-12 h-12 object-contain"
      />
    );
  }
  
  // Show animated gradient for uncloned/custom_branded mood without custom image
  if (moodData.isCustom || moodData.value === 'uncloned' || moodData.value === 'custom_branded') {
    return <UnClonedMoodIcon size="lg" />;
  }
  
  return <span className={className}>{getEmoji ? getEmoji(moodData.emoji) : moodData.emoji}</span>;
};

const EntryCard = ({ entry, onEdit, onDelete, onView, index, clientBranding }) => {
  const { getEmoji } = useTheme();
  const { isFeatureEnabled, isB2BUser, customMood } = useFeatures();
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  // Get mood data - use custom mood if entry.mood is "uncloned" or "custom_branded" and custom mood is configured
  let moodData = MOOD_EMOJIS.find((m) => m.value === entry.mood);
  if ((entry.mood === 'uncloned' || entry.mood === 'custom_branded') && customMood?.enabled) {
    moodData = {
      value: entry.mood,
      label: customMood.label || 'Custom',
      emoji: customMood.imageUrl ? null : '✨',
      imageUrl: customMood.imageUrl
    };
  }
  
  const pdfEnabled = isFeatureEnabled('export_pdf');
  
  const handleDownloadPdf = (e) => {
    e.stopPropagation();
    if (pdfEnabled) {
      downloadEntryAsPdf(entry, clientBranding, customMood);
    } else if (!isB2BUser) {
      // Only show pricing modal for non-B2B users
      setShowPricingModal(true);
    }
  };

  return (
    <div
      className="glass-card p-6 entry-card text-center cursor-pointer hover:border-[var(--theme-primary)]/50 transition-all"
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`entry-card-${entry.id}`}
      onClick={() => onView(entry)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1" />
        <div className="flex flex-col items-center gap-2">
          <MoodDisplay moodData={moodData} getEmoji={getEmoji} />
          {entry.title && (
            <h3 className="text-2xl font-black gradient-text">{entry.title}</h3>
          )}
          <span className="date-badge">{entry.date}</span>
          <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
            {moodData?.label || "Feeling"}
          </p>
        </div>
        <div className="flex-1 flex justify-end">
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="text-[var(--theme-primary)] hover:bg-[var(--theme-background-secondary)]"
              data-testid={`entry-menu-${entry.id}`}
            >
              <MoreVertical className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-[var(--theme-background)] border-[var(--theme-primary)]/30">
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onView(entry); }}
              className="hover:bg-[var(--theme-background-secondary)] cursor-pointer"
              style={{ color: 'var(--theme-text)' }}
              data-testid={`view-entry-${entry.id}`}
            >
              <Sun className="w-4 h-4 mr-2" />
              View
            </DropdownMenuItem>
            {/* Only show Download PDF if enabled OR if non-B2B user (who can upgrade) */}
            {(pdfEnabled || !isB2BUser) && (
            <DropdownMenuItem
              onClick={handleDownloadPdf}
              className="hover:bg-[var(--theme-background-secondary)] cursor-pointer"
              style={{ color: 'var(--theme-text)' }}
              data-testid={`download-entry-${entry.id}`}
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
              {!pdfEnabled && !isB2BUser && (
                <Lock className="w-3 h-3 ml-auto text-[var(--theme-primary)]" />
              )}
            </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onEdit(entry); }}
              className="hover:bg-[var(--theme-background-secondary)] cursor-pointer"
              style={{ color: 'var(--theme-text)' }}
              data-testid={`edit-entry-${entry.id}`}
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
              className="text-red-400 hover:bg-[var(--theme-background-secondary)] cursor-pointer"
              data-testid={`delete-entry-${entry.id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>
      
      {/* Only show "Click to read" hint, not actual content */}
      <p className="text-sm mt-2" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>Tap to read entry</p>
      
      {/* Pricing Modal - only for non-B2B users */}
      {!isB2BUser && (
        <PricingModal 
          isOpen={showPricingModal}
          onClose={() => setShowPricingModal(false)}
        />
      )}
    </div>
  );
};

export default EntryCard;
